import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, setTokens } from '@/lib/api'
import { t } from '@/lib/i18n'
import { useLang } from '@/hooks/use-lang'
import type { AuthResponse } from '@/lib/types'

// Only allow same-origin relative paths so a tampered ?return_to= cannot turn
// the post-login redirect into an open redirect to an attacker-controlled site.
function safeReturnTo(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
    return '/'
  }
  try {
    const u = new URL(raw, window.location.origin)
    if (u.origin !== window.location.origin) return '/'
    return u.pathname + u.search + u.hash
  } catch {
    return '/'
  }
}

export function AuthCallbackPage() {
  useLang() // re-render on lang change
  const [searchParams] = useSearchParams()
  const [error, setError] = useState('')

  useEffect(() => {
    const code = searchParams.get('code')
    if (!code) {
      setError(t('missingExchangeCode'))
      return
    }

    api.post<AuthResponse>('/api/auth/exchange', { code })
      .then((res) => {
        setTokens(res.access_token)
        window.location.href = safeReturnTo(searchParams.get('return_to'))
      })
      .catch(() => {
        setError(t('linkExpired'))
      })
  }, [searchParams])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
        <div className="text-center space-y-4">
          <p className="text-sm" style={{ color: 'var(--red)' }}>{error}</p>
          <a href="/login" className="text-sm font-medium" style={{ color: 'var(--accent)' }}>
            {t('backToLogin')}
          </a>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
      <p className="text-sm" style={{ color: 'var(--muted)' }}>{t('signingIn')}</p>
    </div>
  )
}
